/**
 * Phase 5.2c — Scheduler control UI.
 *
 * Surfaces:
 *   • Global pause/resume (kill switch).
 *   • Per-account pause/resume, cadence (posts/day), niche tags editor.
 *   • Live "next mode target" + posted today + last post timestamp.
 *   • Upcoming queue (candidate videos per account).
 *   • Recent scheduler-originated posts (last 7 days).
 *   • A/B Tournaments — running + completed list with winner score.
 *   • Audit log of pause/resume/force_tick actions.
 *
 * Operator-facing, dense, dark. No fluff.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, formatApiError } from "@/contexts/AuthContext";
import { PageHeader, PageBody, ErrorBlock } from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Bot, Play, Pause, RefreshCw, Zap, Hash, Clock, Trophy,
  CheckCircle2, XCircle, AlertTriangle, ListChecks, Settings2, X,
} from "lucide-react";

function fmtAgo(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch { return "—"; }
}

function StatusBadge({ status }) {
  const styles = {
    posted: "border-emerald-700/60 bg-emerald-500/10 text-emerald-300",
    uploading: "border-sky-700/60 bg-sky-500/10 text-sky-300",
    failed: "border-rose-700/60 bg-rose-500/10 text-rose-300",
    skipped: "border-zinc-700 bg-zinc-900 text-zinc-400",
    running: "border-amber-700/60 bg-amber-500/10 text-amber-300",
    complete: "border-emerald-700/60 bg-emerald-500/10 text-emerald-300",
    scheduled: "border-sky-700/60 bg-sky-500/10 text-sky-300",
  };
  return (
    <Badge variant="outline"
           className={`${styles[status] || "border-zinc-700 text-zinc-400"} text-[10px] mono`}>
      {status || "—"}
    </Badge>
  );
}

// Phase 5.4 Bundle 2 C3 — curated niche picker replaces the free-text
// chip editor. Operator picks ONE primary_niche from the taxonomy and
// then any subset of that niche's sub_pillars. The backend derives the
// `niche_tags` array consumed by the scheduler matcher.
//
// Falls back to the old free-text editor as a deprecated escape hatch
// ONLY if /api/niches returns empty (defensive — should never happen
// since niche_taxonomy.py is statically defined).
function NichePicker({ primary, subPillars, niches, suggestion, onChange }) {
  const current = niches.find((n) => n.key === primary) || null;
  const setPrimary = (key) => {
    const next = niches.find((n) => n.key === key);
    // Auto-select all sub_pillars of the newly chosen niche on first set,
    // otherwise clear (so re-picking doesn't carry over invalid pillars).
    onChange({
      primary_niche: key,
      sub_pillars: next ? next.sub_pillars.slice(0, 3) : [],
    });
  };
  const toggleSub = (s) => {
    const has = subPillars.includes(s);
    const next = has ? subPillars.filter((x) => x !== s) : [...subPillars, s];
    onChange({ primary_niche: primary, sub_pillars: next.slice(0, 7) });
  };
  return (
    <div className="space-y-2" data-testid="niche-picker">
      <div className="flex items-center gap-2 flex-wrap">
        <label className="text-[10px] mono uppercase text-zinc-500">Primary niche</label>
        {suggestion && !primary && (
          <button onClick={() => setPrimary(suggestion)}
                  className="text-[10px] mono text-amber-300 hover:text-amber-200 underline-offset-2 hover:underline"
                  data-testid="niche-suggestion-apply">
            apply suggestion → {suggestion}
          </button>
        )}
      </div>
      <select
        value={primary || ""}
        onChange={(e) => setPrimary(e.target.value)}
        className="w-full h-8 bg-zinc-950 border border-zinc-700 rounded text-xs text-zinc-100 px-2"
        data-testid="niche-primary-select">
        <option value="">— choose a niche —</option>
        {niches.map((n) => (
          <option key={n.key} value={n.key}>{n.display}</option>
        ))}
      </select>
      {current && (
        <>
          <div className="text-[10px] mono uppercase text-zinc-500 pt-1">
            Sub-pillars <span className="text-zinc-600 normal-case">(pick 2–5)</span>
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="niche-sub-pillars">
            {current.sub_pillars.map((s) => {
              const on = subPillars.includes(s);
              return (
                <button key={s} onClick={() => toggleSub(s)}
                  className={`text-[10px] mono px-2 py-0.5 rounded border transition-colors ${on
                    ? "bg-emerald-500/15 text-emerald-200 border-emerald-700/60"
                    : "bg-zinc-900 text-zinc-400 border-zinc-700 hover:text-zinc-200"}`}
                  data-testid={`niche-sub-${s}`}>
                  #{s}
                </button>
              );
            })}
          </div>
          {current.evergreen_hashtags?.length > 0 && (
            <div className="pt-1.5">
              <div className="text-[10px] mono uppercase text-zinc-500 mb-0.5">
                Evergreen hashtags <span className="normal-case text-zinc-600">(auto-attached to every post)</span>
              </div>
              <div className="flex flex-wrap gap-1" data-testid="niche-evergreens">
                {current.evergreen_hashtags.map((tag) => (
                  <span key={tag}
                        className="text-[10px] mono px-1.5 py-0.5 rounded border border-zinc-800 bg-zinc-950/50 text-zinc-500">
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function NicheTagEditor({ tags, onChange, pending, setPending }) {
  // BUGFIX 2026-06-10 — pending input state is LIFTED to the parent
  // (AccountRow) so save() can synchronously merge any uncommitted text
  // into the niche_tags array before PATCH. Previous local-state design
  // had a closure-freshness race: blur → setState scheduled, click →
  // save() reads stale draft → PATCH ships without the just-typed tag.
  // Symptom Matt saw on prod: typed tags appear in UI, never reach DB.
  const commit = () => {
    const t = (pending || "").trim().toLowerCase().replace(/#/g, "");
    if (!t || tags.includes(t) || tags.length >= 20) return;
    onChange([...tags, t]);
    setPending("");
  };
  const remove = (t) => onChange(tags.filter((x) => x !== t));
  return (
    <div className="flex flex-wrap items-center gap-1.5 p-2 bg-zinc-950 border border-zinc-800 rounded min-h-[36px]">
      {tags.map((t) => (
        <span key={t}
              className="inline-flex items-center gap-1 text-[10px] mono px-1.5 py-0.5 rounded border border-zinc-700 bg-zinc-900 text-zinc-200">
          #{t}
          <button onClick={() => remove(t)} className="hover:text-rose-400"
                  data-testid={`niche-tag-remove-${t}`}>
            <X className="h-2.5 w-2.5" />
          </button>
        </span>
      ))}
      <input
        value={pending}
        onChange={(e) => setPending(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") { e.preventDefault(); commit(); }
          else if (e.key === " ") { e.preventDefault(); commit(); }
          else if (e.key === "Backspace" && !pending && tags.length) remove(tags[tags.length - 1]);
        }}
        placeholder={tags.length === 0 ? "tag, then Enter / space / comma" : ""}
        className="bg-transparent border-none outline-none text-xs text-zinc-100 flex-1 min-w-[120px]"
        data-testid="niche-tag-input"
      />
    </div>
  );
}

// Pure helper — given the committed tag list + the current pending input
// string, return the FINAL tag array we should ship to the backend. Used
// by save() and toggleActive() so neither path can silently drop the
// text Matt typed but didn't press Enter on.
function mergePendingTag(committedTags, pendingInput) {
  const t = (pendingInput || "").trim().toLowerCase().replace(/#/g, "");
  if (!t) return committedTags;
  if (committedTags.includes(t)) return committedTags;
  if (committedTags.length >= 20) return committedTags;
  return [...committedTags, t];
}

function AccountRow({ acc, onSave, niches, suggestion }) {
  const [draft, setDraft] = useState({
    scheduler_active: acc.scheduler_active,
    posts_per_day: acc.posts_per_day,
    niche_tags: acc.niche_tags || [],
    primary_niche: acc.primary_niche || "",
    sub_pillars: acc.sub_pillars || [],
  });
  // Pending tag input — lifted from NicheTagEditor so save()/toggleActive()
  // can guarantee any uncommitted text is included in the PATCH payload.
  const [pendingTag, setPendingTag] = useState("");

  const dirty = (
    draft.scheduler_active !== acc.scheduler_active
    || draft.posts_per_day !== acc.posts_per_day
    || JSON.stringify(draft.niche_tags) !== JSON.stringify(acc.niche_tags || [])
    || (pendingTag || "").trim().length > 0
  );
  const save = async () => {
    const finalTags = mergePendingTag(draft.niche_tags, pendingTag);
    const payload = { ...draft, niche_tags: finalTags };
    try {
      await axios.patch(`${API}/scheduler/account/${acc.account_id}`, payload);
      setDraft(payload);
      setPendingTag("");
      toast.success(`Saved @${acc.username || acc.account_id}`);
      onSave?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Save failed");
    }
  };
  const toggleActive = async () => {
    // BUGFIX 2026-06-10 — Toggling active used to PATCH only
    // {scheduler_active}, which silently discarded any pending niche_tags
    // the operator had typed in the editor. Now we send the full draft
    // (including any unsaved niche_tags edits PLUS any uncommitted text
    // in the tag input) plus the toggled flag. Pause/resume effectively
    // becomes "save everything and flip active".
    const nextActive = !acc.scheduler_active;
    const finalTags = mergePendingTag(draft.niche_tags, pendingTag);
    const payload = { ...draft, niche_tags: finalTags, scheduler_active: nextActive };
    try {
      await axios.patch(`${API}/scheduler/account/${acc.account_id}`, payload);
      setDraft(payload);
      setPendingTag("");
      toast.success(acc.scheduler_active ? "Paused (and saved niche tags)" : "Resumed (and saved niche tags)");
      onSave?.();
    } catch (e) {
      toast.error("Toggle failed");
    }
  };
  // Phase 5.4 Bundle 2 C3 — save niche via the new PATCH endpoint
  // which validates against the taxonomy AND derives niche_tags +
  // refreshes the account's evergreen library.
  const saveNiche = async () => {
    if (!draft.primary_niche) {
      toast.error("Pick a primary niche first");
      return;
    }
    if (!draft.sub_pillars || draft.sub_pillars.length === 0) {
      toast.error("Pick at least one sub-pillar");
      return;
    }
    try {
      const r = await axios.patch(
        `${API}/scheduler/account/${acc.account_id}/niche`,
        { primary_niche: draft.primary_niche, sub_pillars: draft.sub_pillars });
      setDraft({
        ...draft,
        niche_tags: r.data.niche_tags || draft.niche_tags,
      });
      toast.success(`Niche set: ${draft.primary_niche}`);
      onSave?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Niche save failed");
    }
  };

  const nicheDirty =
    (draft.primary_niche || "") !== (acc.primary_niche || "") ||
    JSON.stringify((draft.sub_pillars || []).sort()) !==
      JSON.stringify((acc.sub_pillars || []).sort());

  return (
    <div className="ap-panel-2 p-3 space-y-2"
         data-testid={`scheduler-account-${acc.account_id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <div className="text-sm font-medium text-zinc-100 truncate">
            @{acc.username || acc.display_name || acc.account_id}
            <span className="ml-2 text-[10px] mono text-zinc-500">{acc.environment}</span>
          </div>
          <div className="text-[11px] mono text-zinc-500 flex items-center gap-3 mt-0.5">
            <span>
              today <span className="text-zinc-200">{acc.posted_today}</span>/<span className="text-zinc-300">{acc.posts_per_day}</span>
            </span>
            <span>last <span className="text-zinc-200">{fmtAgo(acc.last_post_at)}</span></span>
            <span>next: <span className="text-amber-300">{acc.next_mode_target}</span></span>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <Badge variant="outline"
                 className={acc.scheduler_active
                   ? "border-emerald-700/60 bg-emerald-500/10 text-emerald-300 text-[10px]"
                   : "border-zinc-700 text-zinc-500 text-[10px]"}
                 data-testid={`scheduler-status-${acc.account_id}`}>
            {acc.scheduler_active ? "active" : "paused"}
          </Badge>
          <Button size="sm" variant="outline" onClick={toggleActive}
                  className="border-zinc-700 bg-zinc-900 text-zinc-200 text-xs"
                  data-testid={`scheduler-toggle-${acc.account_id}`}>
            {acc.scheduler_active ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-2 border-t border-zinc-800">
        <div>
          <div className="text-[10px] mono uppercase text-zinc-500 mb-1">posts / day (cap 4)</div>
          <Input
            type="number"
            min={1}
            max={4}
            value={draft.posts_per_day}
            onChange={(e) => setDraft({ ...draft, posts_per_day: parseInt(e.target.value, 10) || 1 })}
            className="h-8 bg-zinc-950 border-zinc-700 text-zinc-100 text-xs"
            data-testid={`scheduler-ppd-${acc.account_id}`}
          />
        </div>
        <div>
          <NichePicker
            primary={draft.primary_niche}
            subPillars={draft.sub_pillars}
            niches={niches || []}
            suggestion={suggestion}
            onChange={({ primary_niche, sub_pillars }) =>
              setDraft({ ...draft, primary_niche, sub_pillars })}
          />
          {nicheDirty && (
            <div className="flex justify-end pt-2">
              <Button size="sm" onClick={saveNiche}
                      className="bg-sky-500 hover:bg-sky-400 text-zinc-950 font-semibold text-xs"
                      data-testid={`scheduler-niche-save-${acc.account_id}`}>
                Save niche
              </Button>
            </div>
          )}
        </div>
      </div>
      {dirty && (
        <div className="flex justify-end pt-1">
          <Button size="sm" onClick={save}
                  className="bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-semibold text-xs"
                  data-testid={`scheduler-save-${acc.account_id}`}>
            Save changes
          </Button>
        </div>
      )}
    </div>
  );
}

export default function SchedulerPage() {
  const [state, setState] = useState(null);
  const [queue, setQueue] = useState(null);
  const [audit, setAudit] = useState(null);
  const [tournaments, setTournaments] = useState(null);
  // Phase 5.4 Bundle 2 — curated niche taxonomy + per-account suggestions.
  const [niches, setNiches] = useState([]);
  const [suggestions, setSuggestions] = useState({});
  const [err, setErr] = useState(null);
  const [ticking, setTicking] = useState(false);
  const [tab, setTab] = useState("accounts");

  const loadAll = useCallback(async () => {
    try {
      const [s, q, a, t, n] = await Promise.all([
        axios.get(`${API}/scheduler/state`),
        axios.get(`${API}/scheduler/queue`),
        axios.get(`${API}/scheduler/audit?limit=50`),
        axios.get(`${API}/scheduler/tournaments?limit=20`),
        axios.get(`${API}/niches`),
      ]);
      setState(s.data);
      setQueue(q.data);
      setAudit(a.data);
      setTournaments(t.data);
      setNiches(n.data?.niches || []);
      // Pull a niche suggestion for each account that doesn't have one
      // yet. Fire-and-forget; ignore failures.
      const accs = s.data?.accounts || [];
      const sugMap = {};
      await Promise.all(accs.map(async (acc) => {
        if (acc.primary_niche) return;
        try {
          const r = await axios.get(
            `${API}/scheduler/account/${acc.account_id}/niche-suggestion`);
          if (r.data?.suggestion) sugMap[acc.account_id] = r.data.suggestion;
        } catch { /* suggestion best-effort, ignore */ }
      }));
      setSuggestions(sugMap);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data || { error: "load_failed", detail: String(e) });
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    const tick = async () => {
      try {
        const [s, q, a, t] = await Promise.all([
          axios.get(`${API}/scheduler/state`),
          axios.get(`${API}/scheduler/queue`),
          axios.get(`${API}/scheduler/audit?limit=50`),
          axios.get(`${API}/scheduler/tournaments?limit=20`),
        ]);
        if (!mounted) return;
        setState(s.data); setQueue(q.data);
        setAudit(a.data); setTournaments(t.data);
        setErr(null);
      } catch (e) {
        if (mounted) setErr(e?.response?.data || { error: "load_failed", detail: String(e) });
      }
    };
    tick();
    const id = setInterval(tick, 15000);
    return () => { mounted = false; clearInterval(id); };
  }, []);

  const togglePauseGlobal = async () => {
    if (!state) return;
    try {
      await axios.post(`${API}/scheduler/global`, { paused: !state.paused_globally });
      toast.success(state.paused_globally ? "Resumed globally" : "Paused globally");
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed");
    }
  };

  const forceTick = async () => {
    setTicking(true);
    try {
      const r = await axios.post(`${API}/scheduler/tick`);
      toast.success(`Tick complete · ${r.data.accounts?.length || 0} accounts processed`);
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Tick failed");
    } finally {
      setTicking(false);
    }
  };

  if (err) return <PageBody><ErrorBlock data={err} testid="scheduler-error" /></PageBody>;
  if (!state) return <PageBody><div className="text-sm text-zinc-500">Loading scheduler…</div></PageBody>;

  const cfg = state.config || {};
  const lastTick = state.last_tick || {};
  const lastTickAt = lastTick.finished_at || lastTick.started_at;

  return (
    <>
      <PageHeader
        icon={Bot}
        title="Scheduler"
        subtitle={`Automation engine · ${state.tiktok_env} · tick every ${cfg.interval_seconds}s · 70/30 growth/affiliate`}
        testid="scheduler-header"
        actions={
          <div className="flex items-center gap-1.5">
            <Button size="sm" variant="outline" onClick={forceTick} disabled={ticking}
                    className="border-zinc-700 bg-zinc-900 text-zinc-200 text-xs"
                    data-testid="scheduler-force-tick">
              <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${ticking ? "animate-spin" : ""}`} />
              {ticking ? "Ticking…" : "Force tick"}
            </Button>
            <Button size="sm" onClick={togglePauseGlobal}
                    className={state.paused_globally
                      ? "bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-semibold text-xs"
                      : "bg-rose-500 hover:bg-rose-400 text-zinc-950 font-semibold text-xs"}
                    data-testid="scheduler-global-toggle">
              {state.paused_globally
                ? <><Play className="h-3.5 w-3.5 mr-1.5" /> Resume all</>
                : <><Pause className="h-3.5 w-3.5 mr-1.5" /> Pause all</>}
            </Button>
          </div>
        }
      />

      <PageBody>
        {/* Top status strip */}
        <section className="ap-panel p-4" data-testid="scheduler-status-strip">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
            <div>
              <div className="text-[10px] mono uppercase text-zinc-500 mb-1">Global</div>
              <StatusBadge status={state.paused_globally ? "skipped" : "running"} />
            </div>
            <div>
              <div className="text-[10px] mono uppercase text-zinc-500 mb-1">Last tick</div>
              <div className="text-zinc-200 mono text-[11px]">{fmtAgo(lastTickAt)}</div>
            </div>
            <div>
              <div className="text-[10px] mono uppercase text-zinc-500 mb-1">Accounts</div>
              <div className="text-zinc-200">
                {state.accounts.filter((a) => a.scheduler_active).length}/{state.accounts.length} active
              </div>
            </div>
            <div>
              <div className="text-[10px] mono uppercase text-zinc-500 mb-1">Cap (env)</div>
              <div className="text-zinc-200">
                {state.tiktok_env === "sandbox"
                  ? `${cfg.sandbox_posts_per_day}/day (sandbox)`
                  : `${cfg.max_posts_per_account_per_day}/day`}
              </div>
            </div>
            <div>
              <div className="text-[10px] mono uppercase text-zinc-500 mb-1">Gap</div>
              <div className="text-zinc-200">{cfg.min_gap_hours_per_account}h min</div>
            </div>
          </div>
        </section>

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-zinc-800 -mb-1">
          {[
            ["accounts", "Accounts", Settings2],
            ["queue", "Queue & history", ListChecks],
            ["tournaments", "Tournaments", Trophy],
            ["audit", "Audit", Clock],
          ].map(([key, label, Icon]) => (
            <button key={key}
                    onClick={() => setTab(key)}
                    className={`text-xs px-3 py-2 flex items-center gap-1.5 border-b-2 -mb-px transition ${
                      tab === key
                        ? "border-emerald-400 text-emerald-300"
                        : "border-transparent text-zinc-500 hover:text-zinc-200"
                    }`}
                    data-testid={`scheduler-tab-${key}`}>
              <Icon className="h-3.5 w-3.5" />
              {label}
            </button>
          ))}
        </div>

        {/* Accounts */}
        {tab === "accounts" && (
          <section className="space-y-2" data-testid="scheduler-accounts-panel">
            {state.accounts.length === 0 && (
              <div className="text-xs text-zinc-500 ap-panel p-4">
                No TikTok accounts connected yet. Connect one from Settings → TikTok Accounts.
              </div>
            )}
            {state.accounts.map((acc) => (
              <AccountRow key={acc.account_id} acc={acc} onSave={loadAll}
                          niches={niches}
                          suggestion={suggestions[acc.account_id]} />
            ))}
          </section>
        )}

        {/* Queue */}
        {tab === "queue" && queue && (
          <section className="space-y-3" data-testid="scheduler-queue-panel">
            <div className="ap-panel p-4">
              <h3 className="text-xs mono uppercase text-zinc-500 mb-3">Upcoming candidates</h3>
              {queue.upcoming.length === 0 && (
                <div className="text-xs text-zinc-500">No active accounts.</div>
              )}
              <div className="space-y-1.5">
                {queue.upcoming.map((u) => (
                  <div key={u.account_id}
                       className="flex items-center justify-between gap-2 text-xs ap-panel-2 p-2"
                       data-testid={`queue-upcoming-${u.account_id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="text-zinc-200 truncate">@{u.username || u.account_id}</div>
                      <div className="text-[10px] mono text-zinc-500 truncate">
                        next mode: <span className="text-amber-300">{u.next_mode_target}</span>
                        {u.candidate_product_name
                          ? <> · candidate: <span className="text-zinc-300">{u.candidate_product_name}</span></>
                          : <> · <span className="text-rose-400">no eligible video</span></>}
                      </div>
                    </div>
                    {u.candidate_niche && (
                      <Badge variant="outline" className="border-zinc-700 text-zinc-400 text-[10px] mono">
                        <Hash className="h-2.5 w-2.5 mr-1" />{u.candidate_niche}
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="ap-panel p-4">
              <h3 className="text-xs mono uppercase text-zinc-500 mb-3">
                Recent scheduler posts (7d · {queue.recent_posts.length})
              </h3>
              {queue.recent_posts.length === 0 && (
                <div className="text-xs text-zinc-500">No scheduler-originated posts yet.</div>
              )}
              <div className="space-y-1">
                {queue.recent_posts.map((p) => (
                  <div key={p.id}
                       className="flex items-center justify-between gap-2 text-xs py-1.5 border-b border-zinc-900 last:border-0"
                       data-testid={`queue-post-${p.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="text-zinc-200 truncate">@{p.tiktok_username || p.tiktok_account_id}</div>
                      <div className="text-[10px] mono text-zinc-500 truncate">
                        mode: <span className="text-amber-300">{p.mode}</span>
                        {" · "}{fmtAgo(p.created_at)}
                        {p.error && <span className="ml-2 text-rose-400">· {p.error.slice(0, 60)}</span>}
                      </div>
                    </div>
                    <StatusBadge status={p.status} />
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Tournaments */}
        {tab === "tournaments" && tournaments && (
          <section className="ap-panel p-4 space-y-3" data-testid="scheduler-tournaments-panel">
            <div className="flex items-center justify-between">
              <h3 className="text-xs mono uppercase text-zinc-500">
                A/B Tournaments ({tournaments.items.length}) · weights: views×{cfg.tournament_weights?.views} likes×{cfg.tournament_weights?.likes} comments×{cfg.tournament_weights?.comments} shares×{cfg.tournament_weights?.shares}
              </h3>
            </div>
            {tournaments.items.length === 0 && (
              <div className="text-xs text-zinc-500">
                No tournaments yet. Create one from the Library page once you have 2+ variant videos for the same product.
              </div>
            )}
            <div className="space-y-2">
              {tournaments.items.map((t) => (
                <div key={t.id}
                     className="ap-panel-2 p-3 space-y-1.5"
                     data-testid={`tournament-${t.id}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-sm text-zinc-200 flex items-center gap-2">
                      <Trophy className="h-3.5 w-3.5 text-amber-400" />
                      <span className="mono text-[11px] text-zinc-400">{t.id.slice(0, 8)}</span>
                      <span className="text-[10px] mono text-zinc-500">· {t.mutation_dimension}</span>
                    </div>
                    <StatusBadge status={t.status} />
                  </div>
                  <div className="text-[10px] mono text-zinc-500">
                    started {fmtAgo(t.started_at)} · ends {fmtAgo(t.ends_at)} · {t.variants?.length || 0} variants
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {(t.variants || []).map((v) => (
                      <div key={v.video_id}
                           className={`text-[10px] mono px-1.5 py-0.5 rounded border ${
                             v.is_winner
                               ? "border-amber-500/60 bg-amber-500/10 text-amber-300"
                               : "border-zinc-700 bg-zinc-900 text-zinc-400"
                           }`}>
                        {v.variant_label}: {v.product_name?.slice(0, 30) || v.video_id?.slice(0, 8)}
                        {v.metrics?.views ? ` · ${v.metrics.views}v` : ""}
                        {v.is_winner ? " 👑" : ""}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Audit */}
        {tab === "audit" && audit && (
          <section className="ap-panel p-4" data-testid="scheduler-audit-panel">
            <h3 className="text-xs mono uppercase text-zinc-500 mb-3">
              Audit log (last {audit.count})
            </h3>
            {audit.entries.length === 0 && (
              <div className="text-xs text-zinc-500">No audit entries yet.</div>
            )}
            <div className="space-y-1">
              {audit.entries.map((e, i) => (
                <div key={i}
                     className="flex items-center justify-between gap-2 text-xs py-1.5 border-b border-zinc-900 last:border-0"
                     data-testid={`audit-entry-${i}`}>
                  <div className="min-w-0 flex-1">
                    <span className="text-zinc-200 mono text-[11px]">{e.action}</span>
                    {e.account_id && <span className="text-[10px] mono text-zinc-500 ml-2">acc {e.account_id.slice(0, 8)}</span>}
                    {e.video_id && <span className="text-[10px] mono text-zinc-500 ml-2">video {e.video_id.slice(0, 8)}</span>}
                    {e.error && <span className="text-[10px] text-rose-400 ml-2">· {String(e.error).slice(0, 60)}</span>}
                  </div>
                  <div className="text-[10px] mono text-zinc-500 shrink-0">{fmtAgo(e.at)}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        <div className="text-[10px] mono text-zinc-600 flex items-center gap-2">
          <Zap className="h-3 w-3" /> Sandbox enforces 1 post/day per account; production allows up to {cfg.max_posts_per_account_per_day}.
        </div>
      </PageBody>
    </>
  );
}
